// src/stores/customerDirectoryStore.js
//
// The finished-customer search index (docs/features/FINISHED_CUSTOMER_SEARCH_FEATURE.md).
//
// The dashboards load only active and lead customers; the finished pool (completed + canceled) is 10 MB
// of documents, which is why it was split out. This store reads the server-maintained index instead:
// customerDirectory/finished-{0..3}, about 200 bytes a customer, four documents, one listener. That
// makes every customer findable for the cost of four reads.
//
// LAZY: nothing loads until a search box that opts in asks (subscribe()). Gated three ways, all
// fail-closed: the `finishedCustomerSearch` frontend flag, full customer visibility (the only roles the
// rules let read the index; org owners and sales/setters stay scoped to their own customers), and a
// signed-in user. Entries are shaped like the search items CustomerSearchAutocomplete builds, plus
// `finished: true`.

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { collection } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'
import { useUserStore } from '@/stores/userStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { useFrontendFlagsStore } from '@/stores/frontendFlagsStore'
import { derivePhoneLast10 } from '@/utils/formatters'

export const FINISHED_SEARCH_FLAG = 'finishedCustomerSearch'

/** "Complete" / "Canceled" for the result chip, from the entry's base status or its pipeline. */
function statusLabel(entry) {
  if (entry.st) return entry.st
  return entry.pl === 'dead' ? 'Canceled' : 'Complete'
}

function toItem(id, entry) {
  const phone = entry.p || ''
  return {
    id,
    name: entry.n || id,
    address: entry.a || '',
    email: entry.e || '',
    phone,
    phoneLast10: derivePhoneLast10(phone) || '',
    elevateProjectId: entry.ep || '',
    pipeline: entry.pl || '',
    statusLabel: statusLabel(entry),
    finished: true,
  }
}

export const useCustomerDirectoryStore = defineStore('customerDirectory', () => {
  const entriesById = ref({})
  const isLoading = ref(false)
  const hasLoaded = ref(false)
  const error = ref(null)

  let unsubscribe = null
  let subscribePromise = null

  const entries = computed(() => Object.values(entriesById.value))

  /** May this session read the index at all? */
  async function isAllowed() {
    const userStore = useUserStore()
    if (!userStore.user) return false
    const flags = useFrontendFlagsStore()
    await flags.whenLoaded()
    if (!flags.isFlagOn(FINISHED_SEARCH_FLAG, false)) return false
    const permStore = usePermissionsStore()
    await permStore.waitForInit()
    return permStore.hasFullVisibility('customers', userStore.roles, userStore.isSuperAdmin)
  }

  /**
   * Start the one listener (idempotent). Resolves true once the index has loaded, false when this
   * session may not read it. A refusal is not memoized, so a later call retries (the flag may flip).
   */
  function subscribe() {
    if (subscribePromise) return subscribePromise
    subscribePromise = (async () => {
      if (!(await isAllowed())) {
        subscribePromise = null
        return false
      }
      isLoading.value = true
      error.value = null
      return new Promise(resolve => {
        unsubscribe = onSnapshot(
          collection(db, 'customerDirectory'),
          snap => {
            const next = {}
            snap.forEach(shard => {
              const map = shard.data()?.entries || {}
              for (const [id, entry] of Object.entries(map)) next[id] = toItem(id, entry || {})
            })
            entriesById.value = next
            isLoading.value = false
            hasLoaded.value = true
            resolve(true)
          },
          err => {
            // A denied or failed read leaves search exactly as it was without the index.
            console.error('[customerDirectoryStore] snapshot error:', err)
            error.value = err
            isLoading.value = false
            if (unsubscribe) unsubscribe()
            unsubscribe = null
            subscribePromise = null
            resolve(false)
          }
        )
      })
    })()
    return subscribePromise
  }

  /**
   * Ids of finished customers whose name, address, email, phone or P-number contains `text`
   * (case-insensitive; phone compared on digits). Same matching as the Finished page's filter.
   */
  function matchIds(text, limit = 30) {
    const q = String(text || '')
      .trim()
      .toLowerCase()
    if (!q) return []
    const digits = q.replace(/\D/g, '')
    const out = []
    for (const e of entries.value) {
      const hit =
        e.name.toLowerCase().includes(q) ||
        e.address.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        e.elevateProjectId.toLowerCase().includes(q) ||
        (digits.length >= 3 && e.phoneLast10.includes(digits))
      if (hit) {
        out.push(e.id)
        if (out.length >= limit) break
      }
    }
    return out
  }

  function unsubscribeStore() {
    if (unsubscribe) unsubscribe()
    unsubscribe = null
    subscribePromise = null
    entriesById.value = {}
    hasLoaded.value = false
    isLoading.value = false
    error.value = null
  }

  return {
    entries,
    entriesById,
    isLoading,
    hasLoaded,
    error,
    subscribe,
    matchIds,
    unsubscribe: unsubscribeStore,
  }
})
